import { useQuery } from '@tanstack/react-query'
import { 
  Building2, 
  CreditCard, 
  TrendingUp,
  DollarSign,
  Users,
  Calendar,
  Activity,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react'
import api from '../lib/api'
import { formatCurrency } from '../lib/utils'

const StatCard = ({ title, value, change, icon: Icon, trend = 'up' }) => {
  const trendColor = trend === 'up' ? 'text-green-600' : 'text-red-600'
  const TrendIcon = trend === 'up' ? ArrowUpRight : ArrowDownRight

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
          {change && (
            <p className={`text-sm mt-1 flex items-center gap-1 ${trendColor}`}>
              <TrendIcon className="h-3 w-3" />
              {change} from last month
            </p>
          )}
        </div>
        <div className="p-3 rounded-full bg-primary/10">
          <Icon className="h-6 w-6 text-primary" />
        </div>
      </div>
    </div>
  )
}

export default function DashboardPage() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const response = await api.get('/admin/dashboard/stats')
      return response.data.data // Backend returns { success, data }
    }
  })

  const { data: recentBusinessesData } = useQuery({
    queryKey: ['recent-businesses'],
    queryFn: async () => {
      const response = await api.get('/admin/tenants?limit=5&sort=created_at:desc')
      return response.data.data // Backend returns { success, data: { businesses: [...], pagination: {...} } }
    }
  })

  // Note: We use the activeTrials count from stats instead of a separate query
  // since the subscriptions endpoint doesn't return individual trial records
  
  // Extract arrays from nested data
  const recentBusinesses = recentBusinessesData?.businesses || []

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">SaaS platform overview</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card p-6 animate-pulse">
              <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-muted rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-muted rounded w-full"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground">SaaS platform overview and key metrics</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Businesses"
          value={stats?.totalBusinesses || 0}
          change="+12%"
          icon={Building2}
          trend="up"
        />
        <StatCard
          title="Monthly Recurring Revenue"
          value={formatCurrency(stats?.monthlyRevenue || 0)}
          change="+8%"
          icon={DollarSign}
          trend="up"
        />
        <StatCard
          title="Active Businesses"
          value={stats?.activeBusinesses || 0}
          change="+15%"
          icon={CreditCard}
          trend="up"
        />
        <StatCard
          title="Active Trials"
          value={stats?.activeTrials || 0}
          change="-2%"
          icon={Calendar}
          trend="down"
        />
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatCard
          title="Total Users"
          value={stats?.totalUsers || 0}
          change="+22%"
          icon={Users}
          trend="up"
        />
        <StatCard
          title="Active Users (30d)"
          value={stats?.activeUsers || 0}
          change="+18%"
          icon={Activity}
          trend="up"
        />
        <StatCard
          title="Churn Rate"
          value={`${stats?.churnRate || 0}%`}
          change="+2%"
          icon={TrendingUp}
          trend="down"
        />
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-4">Recent Businesses</h3>
          <div className="space-y-4">
            {recentBusinesses.length > 0 ? (
              recentBusinesses.map((business) => (
                <div key={business.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{business.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {business.subdomain}.easica.com
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">
                      {new Date(business.created_at).toLocaleDateString()}
                    </p>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      business.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {business.status}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-muted-foreground text-center py-4">No recent businesses</p>
            )}
          </div>
        </div>

        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-4">Active Trials</h3>
          <div className="flex flex-col items-center justify-center py-8">
            <div className="h-20 w-20 rounded-full bg-blue-100 flex items-center justify-center mb-4">
              <Calendar className="h-10 w-10 text-blue-600" />
            </div>
            <p className="text-4xl font-bold text-blue-600 mb-2">{stats?.activeTrials || 0}</p>
            <p className="text-sm text-muted-foreground">Businesses on trial</p>
            <p className="text-xs text-muted-foreground mt-2">
              View detailed trial information in Subscription Management
            </p>
          </div>
        </div>
      </div>

      {/* Plan Distribution */}
      {stats?.planDistribution && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-4">Plan Distribution</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {stats.planDistribution.map((plan) => (
              <div key={plan.name} className="text-center">
                <div className="text-2xl font-bold mb-1">{plan.count}</div>
                <div className="text-sm text-muted-foreground mb-2">
                  {plan.display_name}
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full" 
                    style={{ 
                      width: `${stats.totalBusinesses > 0 ? (plan.count / stats.totalBusinesses) * 100 : 0}%` 
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
